import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import { Button } from 'components/Button/Button'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { AutonomicsResourceProfile } from 'utils/interfaces'
import {
  OdysseyStartSettings,
  mergeOdysseySettings,
  recommendedPctFromProfile,
} from 'utils/odysseySettings'

import style from './PrerequisitesSettingsForm.module.css'

type Props = {
  value: OdysseyStartSettings
  profile?: AutonomicsResourceProfile | null
  disabled?: boolean
  className?: string
  hideDirtyWater?: boolean
  onChange: (next: OdysseyStartSettings) => void
}

/** Shared Prerequisites fields — same UI for scenario modal and Odyssey task tab. */
const PrerequisitesSettingsForm: React.FC<Props> = ({
  value,
  profile,
  disabled = false,
  className,
  hideDirtyWater = false,
  onChange,
}) => {
  const { t } = useTranslation()
  const recommended = useMemo(() => recommendedPctFromProfile(profile), [profile])

  const setField = <K extends keyof OdysseyStartSettings>(key: K, fieldValue: OdysseyStartSettings[K]) => {
    onChange({ ...value, [key]: fieldValue })
  }

  const onPctChange = (key: 'battery' | 'clean_water_level' | 'dirty_water_level') => (e: any) => {
    const raw = String(e?.target?.value ?? '')
    if (raw.trim() === '') {
      setField(key, 0)
      return
    }
    const n = Number(raw)
    if (!Number.isFinite(n)) return
    setField(key, Math.min(100, Math.max(0, n)))
  }

  const applyRecommended = () => {
    onChange(
      mergeOdysseySettings(
        {
          ...value,
          ...(recommended.battery != null ? { battery: recommended.battery } : {}),
          ...(recommended.clean_water_level != null
            ? { clean_water_level: recommended.clean_water_level }
            : {}),
          ...(!hideDirtyWater && recommended.dirty_water_level != null
            ? { dirty_water_level: recommended.dirty_water_level }
            : {}),
        },
        profile
      )
    )
  }

  const hasAnyRecommended =
    recommended.battery != null ||
    recommended.clean_water_level != null ||
    (!hideDirtyWater && recommended.dirty_water_level != null)

  return (
    <div className={className}>
      <div className={style.checkRow}>
        <FormControlLabel
          className={style.checkLabel}
          control={
            <Checkbox
              size="small"
              checked={value.internet}
              disabled={disabled}
              onChange={(_, checked) => setField('internet', checked)}
              sx={{ color: 'var(--text-secondary)', '&.Mui-checked': { color: 'var(--accent, #2f6fed)' } }}
            />
          }
          label={t('prerequisites.require_internet')}
        />
        <FormControlLabel
          className={style.checkLabel}
          control={
            <Checkbox
              size="small"
              checked={value.charger}
              disabled={disabled}
              onChange={(_, checked) => setField('charger', checked)}
              sx={{ color: 'var(--text-secondary)', '&.Mui-checked': { color: 'var(--accent, #2f6fed)' } }}
            />
          }
          label={t('prerequisites.require_charger')}
        />
      </div>

      <div className={style.row}>
        <TextFieldDescribed
          className={style.textfield}
          description={t('prerequisites.min_battery')}
          placeholder="0–100"
          type="number"
          rightLabel="%"
          value={Math.round(value.battery)}
          disabled={disabled}
          onChange={onPctChange('battery')}
        />
      </div>
      {recommended.battery != null && (
        <div className={style.hint}>{t('prerequisites.recommended', { value: recommended.battery })}</div>
      )}

      <div className={style.row}>
        <TextFieldDescribed
          className={style.textfield}
          description={t('prerequisites.min_clean_water')}
          placeholder="0–100"
          type="number"
          rightLabel="%"
          value={Math.round(value.clean_water_level)}
          disabled={disabled}
          onChange={onPctChange('clean_water_level')}
        />
      </div>
      {recommended.clean_water_level != null && (
        <div className={style.hint}>
          {t('prerequisites.recommended', { value: recommended.clean_water_level })}
        </div>
      )}

      {!hideDirtyWater && (
        <>
          <div className={style.row}>
            <TextFieldDescribed
              className={style.textfield}
              description={t('prerequisites.max_dirty_water')}
              placeholder="0–100"
              type="number"
              rightLabel="%"
              value={Math.round(value.dirty_water_level)}
              disabled={disabled}
              onChange={onPctChange('dirty_water_level')}
            />
          </div>
          {recommended.dirty_water_level != null && (
            <div className={style.hint}>
              {t('prerequisites.recommended', { value: recommended.dirty_water_level })}
            </div>
          )}
        </>
      )}

      {hasAnyRecommended && (
        <div className={style.recommendedRow}>
          <Button.Outlined className={style.btnRecommended} onClick={applyRecommended} disabled={disabled}>
            {t('prerequisites.use_recommended')}
          </Button.Outlined>
        </div>
      )}
    </div>
  )
}

export default PrerequisitesSettingsForm
